/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSearch, 
  faCalendar, 
  faUser, 
  faClock, 
  faArrowRight,
  faTag,
  faHome,
  faBook,
  faBriefcase,
  faGraduationCap,
  faChartLine,
  faLightbulb,
  faGlobe,
  faLaptop,
  faHandshake,
  faChevronDown,
  faFilter
} from '@fortawesome/free-solid-svg-icons';
import { fetchPublicBlogs } from '../api/blogApi';
import { buildApiUrl } from '../config/api';
import '../styles/Blog.css';

const Blog = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [visiblePosts, setVisiblePosts] = useState(9);
  const [isLoading, setIsLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [blogPosts, setBlogPosts] = useState([]);
  const [loadingBlogs, setLoadingBlogs] = useState(true);

  const categories = [
    { id: 'all', name: 'All Posts', icon: faBook, count: 0 },
    { id: 'career', name: 'Career Development', icon: faChartLine, count: 0 },
    { id: 'job-search', name: 'Job Search', icon: faBriefcase, count: 0 },
    { id: 'interview', name: 'Interview Prep', icon: faHandshake, count: 0 },
    { id: 'remote', name: 'Remote Work', icon: faLaptop, count: 0 },
    { id: 'salary', name: 'Salary & Negotiation', icon: faLightbulb, count: 0 },
    { id: 'industry', name: 'Industry Insights', icon: faGlobe, count: 0 }
  ];

  // Load blogs from API
  useEffect(() => {
    const loadBlogs = async () => {
      setLoadingBlogs(true);
      try {
        // Always fetch all blogs, filtering is done client-side
        const blogs = await fetchPublicBlogs('all', '');
        setBlogPosts(blogs || []);
      } catch (error) {
        console.error('Failed to load blogs:', error);
        setBlogPosts([]);
      } finally {
        setLoadingBlogs(false);
      }
    };
    
    loadBlogs();
  }, []); // Only load once on mount

  // Use only API blogs (no fallback)
  const activeBlogPosts = blogPosts;

  const filteredPosts = activeBlogPosts.filter(post => {
    const matchesSearch = searchTerm ? (
      post.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.excerpt?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (Array.isArray(post.tags) && post.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())))
    ) : true;
    const matchesCategory = selectedCategory === 'all' || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const displayedPosts = filteredPosts.slice(0, visiblePosts);

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  const loadMore = () => {
    setIsLoading(true);
    setTimeout(() => {
      setVisiblePosts(prev => Math.min(prev + 6, filteredPosts.length));
      setIsLoading(false);
    }, 500);
  };

  useEffect(() => {
    setVisiblePosts(9);
  }, [selectedCategory, searchTerm]);

  // Update category counts based on loaded blogs
  const categoriesWithCounts = useMemo(() => {
    const posts = blogPosts;
    return categories.map(cat => {
      if (cat.id === 'all') {
        return { ...cat, count: posts.length };
      }
      return { ...cat, count: posts.filter(b => b.category === cat.id).length };
    });
  }, [blogPosts]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDropdownOpen && !event.target.closest('.blog-filter-dropdown-wrapper')) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const getCategoryIcon = (categoryId) => {
    const category = categoriesWithCounts.find(c => c.id === categoryId);
    return category ? category.icon : faBook;
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return 'https://images.unsplash.com/photo-1499750310107-5fef28a66643?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80';
    if (/^https?:\/\//i.test(imagePath)) {
      return imagePath;
    }
    // Handle uploaded images
    const normalized = imagePath.startsWith('/') ? imagePath.replace(/^\/+/, '') : imagePath;
    if (normalized.startsWith('uploads/')) {
      const uploadPath = normalized.replace(/^uploads\//, '');
      return buildApiUrl(`/api/users/uploads/${uploadPath}`);
    }
    return buildApiUrl(imagePath.startsWith('/') ? imagePath : `/${imagePath}`);
  };

  return (
    <div className="blog-page">
      {/* Back to Home Button */}
      <button
        type="button"
        onClick={() => navigate('/')}
        className="blog-back-home-btn"
      >
        <FontAwesomeIcon icon={faHome} />
        Back to Home
      </button>

      {/* Hero Section */}
      <section className="blog-hero">
        <div className="blog-hero-content">
          <h1 className="blog-hero-title">
            AksharJobs Blog
          </h1>
        </div>
      </section>

      {/* Main Content */}
      <div className="blog-container">
        {/* Main Blog Content */}
        <main className="blog-main">
          <div className="blog-header">
            <div className="blog-header-top">
              <h2 className="blog-section-title">
                {selectedCategory === 'all' 
                  ? 'All Articles' 
                  : categoriesWithCounts.find(c => c.id === selectedCategory)?.name}
              </h2>
              <div className="blog-filter-dropdown-wrapper">
                <button 
                  className="blog-filter-dropdown-btn"
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                >
                  <FontAwesomeIcon icon={faFilter} />
                  Filter
                  <FontAwesomeIcon icon={faChevronDown} className={isDropdownOpen ? 'rotate' : ''} />
                </button>
                {isDropdownOpen && (
                  <div className="blog-filter-dropdown">
                    <div className="dropdown-search">
                      <div className="search-wrapper">
                        <input
                          type="text"
                          placeholder="Search articles..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="search-input"
                        />
                      </div>
                    </div>
                    <div className="dropdown-categories">
                      <h3 className="dropdown-title">
                        <FontAwesomeIcon icon={faTag} />
                        Categories
                      </h3>
            <div className="category-list">
              {categoriesWithCounts.map(category => (
                <button
                  key={category.id}
                  className={`category-item ${selectedCategory === category.id ? 'active' : ''}`}
                  onClick={() => {
                    setSelectedCategory(category.id);
                    setIsDropdownOpen(false);
                  }}
                >
                  <div className="category-content">
                    <FontAwesomeIcon icon={category.icon} className="category-icon" />
                    <span className="category-name">{category.name}</span>
                  </div>
                  <span className="category-count">{category.count}</span>
                </button>
              ))}
            </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <p className="blog-results-count">
              {filteredPosts.length} {filteredPosts.length === 1 ? 'article' : 'articles'} found
            </p>
          </div>

          {loadingBlogs ? (
            <div className="blog-loading">
              <p>Loading blogs...</p>
            </div>
          ) : displayedPosts.length > 0 ? (
            <>
              <div className="blog-grid">
                {displayedPosts.map((post, index) => (
                  <article 
                    key={post.id} 
                    className="blog-card"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div className="blog-card-image-wrapper">
                      <img 
                        src={getImageUrl(post.image)} 
                        alt={post.title}
                        className="blog-card-image"
                        loading="lazy"
                      />
                      <div className="blog-card-overlay">
                        <span className="blog-card-category">
                          <FontAwesomeIcon icon={getCategoryIcon(post.category)} />
                          {categoriesWithCounts.find(c => c.id === post.category)?.name || 'Uncategorized'}
                        </span>
                      </div>
                    </div>
                    <div className="blog-card-content">
                      <div className="blog-card-meta">
                        <span className="blog-meta-item">
                          <FontAwesomeIcon icon={faUser} />
                          {post.author || 'Unknown'}
                        </span>
                        <span className="blog-meta-item">
                          <FontAwesomeIcon icon={faCalendar} />
                          {formatDate(post.date || new Date().toISOString())}
                        </span>
                        <span className="blog-meta-item">
                          <FontAwesomeIcon icon={faClock} />
                          {post.readTime || '5 min read'}
                        </span>
                      </div>
                      <h3 className="blog-card-title">{post.title}</h3>
                      <div className="blog-card-tags">
                        {(Array.isArray(post.tags) ? post.tags : []).slice(0, 3).map((tag, idx) => (
                          <span key={idx} className="blog-tag">{tag}</span>
                        ))}
                      </div>
                      <button 
                        className="blog-read-more-btn"
                        onClick={() => navigate(`/blog/${post.id}`)}
                      >
                        Read Article
                        <FontAwesomeIcon icon={faArrowRight} />
                      </button>
                    </div>
                  </article>
                ))}
              </div>

              {visiblePosts < filteredPosts.length && (
                <div className="blog-load-more">
                  <button 
                    className="load-more-btn"
                    onClick={loadMore}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Loading...' : `Load More (${filteredPosts.length - visiblePosts} remaining)`}
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="blog-no-results">
              <FontAwesomeIcon icon={faSearch} className="no-results-icon" />
              <h3>No articles found</h3>
              <p>Try adjusting your search or filter criteria</p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default Blog;
